/**
 * Time + freshness formatting for the Where screen.
 *
 * The whole point of this module is that a stale position must LOOK stale. The
 * poll runs every 15 minutes, so anything older than two missed polls stops
 * being presented as "now" — in words, not just in color.
 */

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** "just now" | "6 min ago" | "3 hours ago" | "2 days ago" */
export function relativeTime(iso: string | null, now = Date.now()): string {
  if (!iso) return 'never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'unknown';

  const diff = Math.max(0, now - then);
  if (diff < MINUTE) return 'just now';
  if (diff < HOUR) {
    const m = Math.round(diff / MINUTE);
    return `${m} min ago`;
  }
  if (diff < DAY) {
    const h = Math.round(diff / HOUR);
    return `${h} hour${h === 1 ? '' : 's'} ago`;
  }
  const d = Math.round(diff / DAY);
  return `${d} day${d === 1 ? '' : 's'} ago`;
}

/** "2:14 PM" */
export function clockTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

export type Freshness = 'fresh' | 'aging' | 'stale';

/** fresh: within two polls. aging: within 3h. stale: older — say so out loud. */
export function freshnessOf(iso: string, now = Date.now()): Freshness {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'stale';
  const diff = now - then;
  if (diff < 30 * MINUTE) return 'fresh';
  if (diff < 3 * HOUR) return 'aging';
  return 'stale';
}

/** Text first, color second. Never signal state with color alone. */
export const FRESHNESS_STYLE: Record<Freshness, { className: string; note: string }> = {
  fresh: { className: 'bg-sage-soft text-sage', note: 'Recent' },
  aging: { className: 'bg-honey-soft text-honey', note: 'May be out of date' },
  stale: { className: 'bg-clay-soft text-clay', note: 'Out of date' },
};

/** Battery text. Low is called "Low", not just colored red. */
export function batteryLabel(battery: number | null): { text: string; low: boolean } | null {
  if (battery === null) return null;
  return { text: `${battery}%`, low: battery <= 20 };
}

// One definition, shared with the avatar component so the two can't drift.
export { initialsOf } from '../../lib/initials';

/** 34.7465, -84.9891 — the honest fallback when Life360 gives us no place name. */
export function coordLabel(lat: number, lng: number): string {
  return `${lat.toFixed(4)}, ${lng.toFixed(4)}`;
}
